import { useEffect, useState } from 'react';
import { Download, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../api/axios';
import { getContactActivityFiles } from '../../services/contacts';
import { useOptionalResourceSubscription } from '../../contexts/NotificationsContext';
import { parseContentDispositionFilename, saveBlobAs } from '../../utils/downloadHelpers';

interface ActivityFile {
  id: string;
  name: string;
  file_size: number | null;
  source_type: 'task' | 'appointment';
  source_title: string;
  can_download: boolean;
  download_path: string;
}

export function ContactActivityFiles({ contactId }: { contactId: string }) {
  const [files, setFiles] = useState<ActivityFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const refresh = () => setRevision(value => value + 1);
  useOptionalResourceSubscription('task', refresh);
  useOptionalResourceSubscription('appointment', refresh);
  useOptionalResourceSubscription('document', refresh);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setDownloadError(false);
    setFiles([]);
    const load = async () => {
      const rows: ActivityFile[] = [];
      let next = true;
      while (next && !cancelled) {
        const { data } = await getContactActivityFiles(contactId, rows.length);
        const page = Array.isArray(data) ? data : data.results;
        rows.push(...page);
        next = !Array.isArray(data) && !!data.next && page.length > 0;
      }
      if (!cancelled) setFiles(rows);
    };
    load().catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [contactId, revision]);

  const download = async (file: ActivityFile) => {
    setDownloading(file.id);
    setDownloadError(false);
    try {
      const response = await api.get(file.download_path, { responseType: 'blob' });
      saveBlobAs(response.data, parseContentDispositionFilename(response.headers?.['content-disposition']) || file.name);
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(null);
    }
  };

  return <section aria-label="File dalle attività" className="min-w-0 mb-4 space-y-2">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h4 className="text-xs font-semibold text-[var(--hexa-text-primary)]">File dalle attività ({files.length})</h4>
      <Button variant="ghost" disabled={loading} onClick={refresh}>Aggiorna file attività</Button>
    </div>
    {loading ? <p className="text-xs text-[var(--hexa-text-muted)]">Caricamento allegati delle attività...</p>
      : error ? <p role="alert" className="text-xs text-red-500">Impossibile caricare gli allegati delle attività. Premi Aggiorna per riprovare.</p>
      : files.length === 0 ? <p className="text-xs text-[var(--hexa-text-muted)]">Nessun allegato nelle attività collegate.</p>
      : <ul className="space-y-2">
        {files.map(file => <li key={file.id} className="flex min-w-0 items-start gap-2 rounded-lg border border-[var(--hexa-border)] p-2.5">
          <FileText className="mt-1 h-4 w-4 shrink-0 text-[var(--hexa-text-muted)]" />
          <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
            <p className="text-xs font-medium text-[var(--hexa-text-primary)]">{file.name}</p>
            <p className="text-[11px] text-[var(--hexa-text-secondary)]">{file.source_type === 'task' ? 'Task' : 'Agenda'} · {file.source_title}</p>
            {file.file_size != null && <p className="text-[10px] text-[var(--hexa-text-muted)]">{Math.ceil(file.file_size / 1024)} KB</p>}
          </div>
          <Button size="icon" variant="ghost" className="h-10 w-10 shrink-0" aria-label={`Scarica ${file.name}`}
            title={file.can_download ? 'Scarica allegato originale' : 'Download non consentito'}
            disabled={!file.can_download || downloading !== null} onClick={() => download(file)}><Download className="h-4 w-4" /></Button>
        </li>)}
      </ul>}
    {downloadError && <p role="alert" className="text-xs text-red-500">Impossibile scaricare il file. Riprova.</p>}
  </section>;
}
